import { useEffect, useState } from 'react';
import { StatusSelect } from '../StatusSelect';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { createNewVariant, getGroupsBySelect, ProductGroupLoadingKeys } from '../../slices/productGroupSlice';
import { SelectDropdown } from '../SelectDropdown';
import { LoaderSpinner } from '../LoaderSpinner';

export const CreateProductVariantSidebar = ({ 
    formId,
    access,
    currentGroup,
    onSuccessSubmit
}) => {
    const { control, register, handleSubmit } = useForm({
        defaultValues: {
            productName: "",
            sku: "",
            barcode: "",
            isAmazon: false,
            inactiveSkus: "",
            status: "ACTIVE"
        },
    });
    const [selectedImage, setSelectedImage] = useState("");

    const {
        groupsBySelect,
        loading: groupsLoading,
        loadingKeys: groupsLoadingKeys
    } = useSelector((state) => state.productGroup)
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getGroupsBySelect({}))
    }, []);

    const onSubmit = async (data) => {
        const dataForCreation = {
            name: data?.productName || "",
            barcode: data?.barcode || "",
            inactive_skus: data?.inactiveSkus || "",
            is_amazon_manually: data?.isAmazon || false,
            is_amazon: data?.isAmazon || false,
            sku: data?.sku || "",
            status: data?.status || "ACTIVE",
            group_id: data?.groupId || null
        };
        const res = dispatch(createNewVariant({ creationData: dataForCreation })).unwrap();
        res?.then(() => {
            onSuccessSubmit?.();
        });
    }

    const groupValuesIsLoading = () => 
        groupsLoading && groupsLoadingKeys.includes(ProductGroupLoadingKeys.getGroupValues);

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)} id={formId}>
                <div>
                    {selectedImage ? (
                        <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center h-full">
                            <img 
                                src={selectedImage} 
                                alt={"Image by variant"}
                                className="max-w-full max-h-48 object-contain min-h-48"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = 'https://placehold.co/300x200/e6e6e6/cccccc?text=No+Image';
                                }}
                            />
                        </div>
                    ) : (
                        <div className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm p-4 flex items-center justify-center h-full">
                            <svg className="w-16 min-h-48 max-h-48 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                    )}
                </div>
                <div className="border-t border-gray-200 pt-4 mt-2">
                    <dl className="divide-y divide-gray-200">
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Group</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2 w-40 relative'>
                                        {/* {groupValuesIsLoading() && (
                                            <div className='absolute z-50 bg-gray-50 w-[95%] border-gray-200 rounded-xl mx-1'>
                                                <LoaderSpinner width='30' height='30' border='3' colorSpinner='black' />
                                            </div>
                                        )} */}
                                        <input
                                            type="hidden"
                                            {...register("groupId")}
                                            value={currentGroup?.value}
                                            readOnly 
                                            className="w-52 bg-white text-sm text-gray-600 py-1 px-2 outline-none"
                                        />
                                        <span className='w-52 text-right'>{currentGroup?.label || ""}</span>
                                        {/* <Controller
                                            id="groupId"
                                            name="groupId"
                                            control={control}
                                            defaultValue={null}
                                            render={({ field }) => (
                                                <SelectDropdown
                                                    options={groupsBySelect}
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    placeholder='Select group'
                                                    selectClassname="!p-1.5"
                                                    selectedValueClassname="whitespace-nowrap text-ellipsis overflow-hidden"
                                                />
                                            )}
                                        /> */}
                                    </div>
                                </div>
                            </dd>
                        </div>         
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Name</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <input
                                            id="productName"
                                            {...register("productName")}
                                            className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                        />
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">SKU</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <input
                                            id="sku"
                                            {...register("sku")}
                                            className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                        />
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Inactive SKU</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <input
                                            id="inactiveSkus"
                                            {...register("inactiveSkus")}
                                            className="w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200"
                                        />
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Barcode</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <input
                                            id="barcode"
                                            {...register("barcode")}
                                            className={`w-52 bg-white border border-gray-300 rounded focus:ring-0 text-sm text-gray-600 py-1 px-2 disabled:bg-gray-200`}
                                        />
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <div className="w-24">
                                            <Controller
                                                id="status"
                                                name="status"
                                                control={control}
                                                defaultValue={null}
                                                render={({ field }) => (
                                                    <StatusSelect
                                                        key={field.value}
                                                        access={access}
                                                        currentStatus={field.value}
                                                        onChange={field.onChange}
                                                    />
                                                )}
                                            />

                                        </div>
                                    </div>
                                </div>
                            </dd>
                        </div>
                        <div className="py-3 flex justify-between">
                            <dt className="text-sm font-medium text-gray-500">Is Amazon</dt>
                            <dd className="text-sm text-gray-900">
                                <div className="relative">
                                    <div className='flex items-center gap-2'>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                id="isAmazon"
                                                {...register("isAmazon")}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            </dd>
                        </div>  
                    </dl>
                </div>
            </form>
        </div>
    );
};